import { request, requestWithPlanWarning } from '@/api/http';
import type { DeleteResult, Search, SearchAclEntry, SearchLinksPage, SearchShare } from '@/api/types';
import type { SearchSourcePipeline } from '@/api/types/searchPipeline';
import type { SearchDiagnostics } from '@/api/types/searchDiagnostics';

export const searchesService = {
  list: (params?: { owner_user_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.owner_user_id) q.set('owner_user_id', params.owner_user_id);
    const qs = q.toString() ? `?${q}` : '';
    return request<Search[]>(`/searches${qs}`);
  },
  create: async (body: object) => {
    const { data, planWarning } = await requestWithPlanWarning<Search>('/searches', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data, planWarning };
  },
  update: (id: string, body: object) =>
    request<Search>(`/searches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string, opts?: { remove_subscriptions?: boolean; delete_scope?: string }) => {
    const params = new URLSearchParams();
    if (opts?.remove_subscriptions) params.set('remove_subscriptions', 'true');
    if (opts?.delete_scope) params.set('delete_scope', opts.delete_scope);
    const q = params.toString() ? `?${params}` : '';
    return request<DeleteResult>(`/searches/${id}${q}`, { method: 'DELETE' });
  },
  links: (
    id: string,
    params?: {
      status?: string;
      delivery_status?: string;
      source?: string;
      source_id?: string;
      recipient_id?: string;
      q?: string;
      page?: number;
      limit?: number;
      offset?: number;
    }
  ) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.delivery_status) q.set('delivery_status', params.delivery_status);
    if (params?.source) q.set('source', params.source);
    if (params?.source_id) q.set('source_id', params.source_id);
    if (params?.recipient_id) q.set('recipient_id', params.recipient_id);
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<SearchLinksPage>(`/searches/${id}/links${qs}`);
  },
  sourcePipeline: (id: string, source?: string) => {
    const q = source ? `?source=${encodeURIComponent(source)}` : '';
    return request<SearchSourcePipeline>(`/searches/${id}/source-pipeline${q}`);
  },
  diagnostics: (id: string) => request<SearchDiagnostics>(`/searches/${id}/diagnostics`),
  listShares: (id: string) => request<SearchShare[]>(`/searches/${id}/shares`),
  upsertShare: (id: string, body: object) =>
    request<SearchShare>(`/searches/${id}/shares`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteShare: (id: string, granteeUserId: string) =>
    request<void>(`/searches/${id}/shares/${granteeUserId}`, { method: 'DELETE' }),
  listAcl: (id: string) => request<SearchAclEntry[]>(`/searches/${id}/acl`),
  setAcl: (
    id: string,
    body: { principalType?: string; principalId: string; permissions: string[] },
  ) =>
    request<SearchAclEntry[]>(`/searches/${id}/acl`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAcl: (id: string, principalId: string, principalType = 'user') => {
    const q = new URLSearchParams({ principalId, principalType });
    return request<void>(`/searches/${id}/acl?${q}`, { method: 'DELETE' });
  },
  transfer: (id: string, newOwnerUserId: string) =>
    request<Search>(`/searches/${id}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerUserId }),
    }),
};
